import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Overline, Loading } from "@/components/shared";

export default function StartHere() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/start-here").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;

  return (
    <div className="max-w-6xl" data-testid="start-here-page">
      <div className="flex items-start justify-between gap-4">
        <Overline>Start Here</Overline>
        {user?.is_admin && (
          <Link to="/app/admin/page/start-here" data-testid="admin-edit-start-here"
            className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/20 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors">
            <Pencil size={13} /> Edit page
          </Link>
        )}
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2 leading-tight">{data.title}</h1>
      <p className="text-xl text-nm-neutral font-light leading-relaxed mt-5">{data.intro}</p>

      <div className="mt-10 space-y-5" data-testid="start-here-body">
        {(data.body || []).map((b, i) => {
          switch (b.t) {
            case "h2":
              return (
                <h2 key={i} className="font-serif text-3xl font-semibold text-nm-ink mt-14 pt-7 border-t border-white leading-snug flex items-center gap-3">
                  <span className="h-7 w-1.5 rounded-full bg-nm-coral shrink-0" />
                  {b.text}
                </h2>
              );
            case "h3":
              return <h3 key={i} className="font-serif text-xl font-semibold text-nm-coral mt-9 leading-snug">{b.text}</h3>;
            case "ul":
              return (
                <ul key={i} className="space-y-2.5 pl-1">
                  {b.items.map((it, k) => (
                    <li key={k} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-nm-primary shrink-0" />{it}
                    </li>
                  ))}
                </ul>
              );
            default:
              return <p key={i} className="text-nm-ink/85 leading-relaxed text-[17px]">{b.text}</p>;
          }
        })}
      </div>

      <div className="mt-12 rounded-3xl border border-nm-highlight/30 bg-gradient-to-b from-[#FCF6E4] to-[#FFFDF8] p-8">
        <div className="flex items-center gap-2 text-nm-ink font-medium"><Compass size={18} className="text-nm-primary" /> Jump to the recommended chapters</div>
        <div className="mt-5 space-y-3">
          {data.recommended.map((c, i) => (
            <Link key={c.slug} to={`/app/chapter/${c.slug}`} data-testid={`sh-rec-${c.slug}`}
              className="group flex items-center gap-4 rounded-2xl bg-white/80 border border-nm-primary/10 px-5 py-4 hover:bg-white hover:shadow-sm transition-all duration-200">
              <span className="font-serif text-2xl text-nm-primary/40 w-8">{i + 1}</span>
              <div className="flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-nm-coral">{c.section_label}</div>
                <div className="font-serif text-lg text-nm-ink">{c.title}</div>
              </div>
              <ArrowRight size={18} className="text-nm-primary group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
